import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Search, Check, Trash2, Download, FileText, Mail, Briefcase, Inbox } from "lucide-react";
import { api, API } from "../lib/api";

const TABS = [
  { key: "quotes", label: "Quote Requests", type: "quote", icon: <FileText size={16} /> },
  { key: "enquiries", label: "Enquiries", type: "enquiry", icon: <Mail size={16} /> },
  { key: "applications", label: "Applications", type: "application", icon: <Briefcase size={16} /> },
];

const StatCard = ({ label, total, fresh, accent }) => (
  <div data-testid={`stat-${label.toLowerCase()}`} className="bg-white rounded-xl border border-neutral-200 p-5">
    <div className="text-xs uppercase tracking-widest text-neutral-500 font-heading">{label}</div>
    <div className="mt-2 flex items-end justify-between">
      <div className="font-display text-4xl font-light text-[#222629] tracking-tighter">{total}</div>
      <div className="text-xs px-2 py-1 rounded-full" style={{ background: accent + "22", color: accent }}>{fresh} new</div>
    </div>
  </div>
);

const Row = ({ item, tab, onToggleHandled, onDelete, onDownload }) => (
  <tr data-testid={`row-${tab.type}-${item.id}`} className={`border-b border-neutral-100 hover:bg-[#F7F8F5]/40 ${item.handled ? "opacity-60" : ""}`}>
    <td className="py-4 px-4">
      <div className="font-heading text-sm text-[#222629]">{item.name}</div>
      <div className="text-xs text-neutral-500">{item.email}</div>
      {item.company && <div className="text-xs text-neutral-500">{item.company}</div>}
    </td>
    <td className="py-4 px-4 text-sm text-neutral-700">
      {tab.type === "quote" && (<><div>{item.product_name}</div>{item.quantity && <div className="text-xs text-neutral-500">Qty: {item.quantity}</div>}</>)}
      {tab.type === "enquiry" && (<><div className="font-heading text-[#12355B]">{item.subject}</div><div className="text-xs text-neutral-500 line-clamp-2 max-w-xs">{item.message}</div></>)}
      {tab.type === "application" && (<><div className="font-heading text-[#12355B]">{item.position}</div>{item.experience_years && <div className="text-xs text-neutral-500">{item.experience_years} yrs exp</div>}</>)}
    </td>
    <td className="py-4 px-4 text-xs text-neutral-500">{new Date(item.created_at).toLocaleString()}</td>
    <td className="py-4 px-4">
      <span data-testid={`row-status-${item.id}`} className={`text-xs px-2 py-1 rounded-full ${item.handled ? "bg-neutral-200 text-neutral-600" : "bg-[#2F9E44]/15 text-[#2F9E44]"}`}>
        {item.handled ? "Handled" : "New"}
      </span>
    </td>
    <td className="py-4 px-4">
      <div className="flex items-center gap-2 justify-end">
        {tab.type === "application" && (
          <button data-testid={`resume-btn-${item.id}`} onClick={() => onDownload(item)} title="Download resume" className="p-2 rounded-full hover:bg-[#12355B]/10 text-[#12355B]"><Download size={14} /></button>
        )}
        <button data-testid={`toggle-handled-${item.id}`} onClick={() => onToggleHandled(item)} title="Toggle handled" className={`p-2 rounded-full hover:bg-[#2F9E44]/15 ${item.handled ? "text-neutral-400" : "text-[#2F9E44]"}`}>
          <Check size={14} />
        </button>
        <button data-testid={`delete-${item.id}`} onClick={() => onDelete(item)} title="Delete" className="p-2 rounded-full hover:bg-[#2F9E44]/15 text-[#2F9E44]"><Trash2 size={14} /></button>
      </div>
    </td>
  </tr>
);

const AdminDashboard = () => {
  const nav = useNavigate();
  const token = localStorage.getItem("rk_admin_token");
  const [tab, setTab] = useState(TABS[0]);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [handled, setHandled] = useState("all");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/admin/stats");
      setStats(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (handled !== "all") params.handled = handled === "true";
      const res = await api.get(`/admin/${tab.key}`, { params });
      setItems(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("rk_admin_token");
        nav("/admin");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [tab, q, handled, nav]);

  useEffect(() => { if (token) { fetchStats(); fetchList(); } }, [token, fetchStats, fetchList]);

  if (!token) return <Navigate to="/admin" replace />;

  const logout = () => {
    localStorage.removeItem("rk_admin_token");
    localStorage.removeItem("rk_admin_email");
    nav("/admin");
  };

  const onToggleHandled = async (item) => {
    try {
      await api.patch(`/admin/${tab.type}/${item.id}/handled`, { handled: !item.handled });
      toast.success(item.handled ? "Marked as new" : "Marked as handled");
      fetchList(); fetchStats();
    } catch { toast.error("Update failed"); }
  };

  const onDelete = async (item) => {
    if (!window.confirm("Delete this record permanently?")) return;
    try {
      await api.delete(`/admin/${tab.type}/${item.id}`);
      toast.success("Deleted");
      fetchList(); fetchStats();
    } catch { toast.error("Delete failed"); }
  };

  const onDownload = async (item) => {
    try {
      const res = await api.get(`/admin/applications/${item.id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = item.resume_filename || "resume.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download resume"); }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5] font-body">
      {/* Sidebar / Topbar */}
      <header className="bg-[#222629] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg p-1.5">
              <img src="/rk-steel-logo.png" alt="R K STEEL" className="h-8 w-auto" />
            </div>
            <div>
              <div className="font-heading font-semibold text-sm">Admin Console</div>
              <div className="text-[10px] tracking-widest uppercase text-[#B8823E]">{localStorage.getItem("rk_admin_email")}</div>
            </div>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="btn-pill text-xs" style={{ background: "transparent", color: "#B8823E", border: "1px solid rgba(242,214,165,0.3)" }}>
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Quotes" total={stats?.quotes?.total ?? "—"} fresh={stats?.quotes?.new ?? 0} accent="#12355B" />
          <StatCard label="Enquiries" total={stats?.enquiries?.total ?? "—"} fresh={stats?.enquiries?.new ?? 0} accent="#2F9E44" />
          <StatCard label="Applications" total={stats?.applications?.total ?? "—"} fresh={stats?.applications?.new ?? 0} accent="#2F9E44" />
        </div>

        {/* Tabs + Filters */}
        <div className="mt-10 flex flex-wrap items-center gap-3">
          {TABS.map(t => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t)}
              className={`btn-pill text-sm ${tab.key === t.key ? "btn-primary" : "btn-ghost"}`}
            >
              {t.icon} {t.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                data-testid="admin-search"
                placeholder="Search…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 pr-4 py-2 rounded-full border border-neutral-300 bg-white text-sm outline-none focus:border-[#12355B] w-64"
              />
            </div>
            <select
              data-testid="admin-filter-handled"
              value={handled}
              onChange={(e) => setHandled(e.target.value)}
              className="px-4 py-2 rounded-full border border-neutral-300 bg-white text-sm outline-none focus:border-[#12355B]"
            >
              <option value="all">All</option>
              <option value="false">New</option>
              <option value="true">Handled</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 bg-white rounded-2xl border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="admin-table">
              <thead className="bg-[#F7F8F5] text-xs uppercase tracking-widest text-neutral-600 font-heading">
                <tr>
                  <th className="py-3 px-4">Contact</th>
                  <th className="py-3 px-4">Details</th>
                  <th className="py-3 px-4">Submitted</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="py-16 text-center text-neutral-500">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={5} className="py-16 text-center text-neutral-500"><Inbox className="inline mr-2" size={16} /> No records.</td></tr>
                ) : (
                  items.map(item => (
                    <Row key={item.id} item={item} tab={tab} onToggleHandled={onToggleHandled} onDelete={onDelete} onDownload={onDownload} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
